import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, ArrowRight, Store, Truck, Building2, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';
import { paymentsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useSmartNotify } from '../components/guidance/SmartNotificationProvider';
import { Button } from '../components/ui/button';

function FundFlowDiagram({ fundFlow }) {
  if (!fundFlow) return null;

  const items = [
    { label: 'Product Cost', amount: fundFlow.product_cost, icon: Store, color: 'bg-violet-100 text-violet-600', to: fundFlow.vendor_name || 'Vendor' },
  ];
  if (fundFlow.delivery_fee > 0) {
    items.push({ label: 'Driver (95%)', amount: fundFlow.driver_receives, icon: Truck, color: 'bg-cyan-100 text-cyan-600', to: 'OVA Driver' });
    items.push({ label: 'Platform Fee (5%)', amount: fundFlow.platform_fee, icon: Building2, color: 'bg-amber-100 text-amber-600', to: 'MMarketing' });
  }

  return (
    <div className="mt-6 p-5 bg-zinc-50 rounded-2xl border border-zinc-200" data-testid="fund-flow-diagram">
      <h3 className="font-display font-semibold text-sm text-zinc-700 mb-4 flex items-center gap-2">
        <Wallet size={16} className="text-zinc-500" /> Where Your Money Goes
      </h3>
      <div className="space-y-3">
        {items.map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.2 }}
            className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.color}`}>
              <item.icon size={16} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">{item.label}</span>
                <span className="font-mono font-bold text-sm text-zinc-900">{formatCurrency(item.amount)}</span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <ArrowRight size={10} className="text-zinc-400" />
                <span className="text-xs text-zinc-400">{item.to}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-700">Total Paid</span>
        <span className="font-mono font-bold text-lg text-zinc-900">{formatCurrency(fundFlow.total_paid)}</span>
      </div>
    </div>
  );
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { triggerSmartNotify } = useSmartNotify();

  const isWalletFunding = paymentType === 'wallet';

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }

    const checkStatus = async (attempts = 0) => {
      try {
        const res = await paymentsAPI.getStatus(sessionId);
        setPaymentData(res.data);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          if (!isWalletFunding) {
            triggerSmartNotify('purchased', 'order', sessionId);
          }
        }
        else if (res.data.status === 'expired') setStatus('failed');
        else if (attempts < 5) setTimeout(() => checkStatus(attempts + 1), 2000);
        else setStatus('failed');
      } catch {
        if (attempts < 5) setTimeout(() => checkStatus(attempts + 1), 2000);
        else setStatus('failed');
      }
    };
    checkStatus();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md text-center">
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-white border border-zinc-200 rounded-2xl flex items-center justify-center">
              <Loader className="text-[#b8ff00] animate-spin" size={40} />
            </div>
            <h1 className="font-display font-bold text-2xl text-zinc-900 mb-2">Processing Payment</h1>
            <p className="text-zinc-500">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.5 }}
              className="w-20 h-20 mx-auto mb-6 bg-green-50 border border-green-200 rounded-2xl flex items-center justify-center">
              <CheckCircle className="text-green-500" size={40} />
            </motion.div>
            <h1 className="font-display font-bold text-2xl text-zinc-900 mb-2">
              {isWalletFunding ? 'Wallet Funded!' : 'Payment Successful!'}
            </h1>
            <p className="text-zinc-500 mb-4">
              {isWalletFunding
                ? 'Your wallet has been funded via your card.'
                : 'Thank you for your purchase. Your order has been confirmed.'}
            </p>
            {paymentData && (
              <div className="bg-white border border-zinc-200 rounded-2xl p-5 mb-4">
                <p className="text-sm text-zinc-500 mb-1">{isWalletFunding ? 'Amount Added' : 'Amount Paid'}</p>
                <p className="font-display font-bold text-3xl text-zinc-900" data-testid="payment-amount">
                  ${paymentData.amount?.toFixed(2)}
                </p>
              </div>
            )}

            {/* Fund Flow Diagram for order payments */}
            {!isWalletFunding && paymentData?.fund_flow && (
              <FundFlowDiagram fundFlow={paymentData.fund_flow} />
            )}

            <div className="flex gap-3 mt-6">
              <Link to={isWalletFunding ? '/wallet' : '/orders'} className="flex-1">
                <Button className="w-full bg-[#b8ff00] text-black hover:bg-[#a8ef00] font-bold rounded-xl py-5" data-testid="continue-btn">
                  {isWalletFunding ? 'Back to Wallet' : 'View Orders'}
                </Button>
              </Link>
              <Link to="/marketplace" className="flex-1">
                <Button variant="outline" className="w-full rounded-xl py-5 border-zinc-300" data-testid="continue-shopping-btn">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-50 border border-red-200 rounded-2xl flex items-center justify-center">
              <XCircle className="text-red-500" size={40} />
            </div>
            <h1 className="font-display font-bold text-2xl text-zinc-900 mb-2">Payment Failed</h1>
            <p className="text-zinc-500 mb-6">Something went wrong with your payment. Please try again.</p>
            <Link to="/">
              <Button className="w-full bg-zinc-900 text-white font-bold rounded-xl py-5" data-testid="try-again-btn">Return Home</Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
